import { graphql } from 'gatsby'
import { getSrc } from 'gatsby-plugin-image'
import * as React from 'react'
import styled from 'styled-components'

import StyledEmbeddedEntry from '../components/EmbeddedEntry/StyledEmbeddedEntry'
import theme from '../components/atoms/theme'
import Container from '../components/container'
import Layout from '../components/layout'
import SEO from '../components/molecules/seo'
import {
  ImageGridBlock,
  ImageGridStyledContainer,
} from '../components/organisms/ImageGridBlock'
import { ChatBot } from '../components/organisms/chatBot'
import { ContentSet } from '../components/organisms/contentSet'
import HeroCarousel from '../components/organisms/heroCarousel'
import { IframeBlock } from '../components/organisms/iframeBlock'
import Spotlight from '../components/organisms/spotlight'
import { isChatbotEnabled } from '../helpers/chatbot'
import GetFocalRatio from '../helpers/getFocalRatio'
import { isContentfulType } from '../lib/contentful'

const { colours, breakpoints } = theme

type HomePageProps = {
  data: Queries.HomePageQuery
}

enum DynamicType {
  EVENT = 'most recent Event with spotlight on homepage',
  PAGE = 'most recent Page with spotlight on homepage',
}

export const BlockContainer = styled.div`
  padding: 0 4rem;

  ${ImageGridStyledContainer} {
    padding: 0 2rem;
  }
`

export const BlockWrapper = styled.div`
  margin: 0 -4rem 2rem;
  padding: 0;
  background-color: ${colours.dark};

  @media only screen and ${breakpoints.fromNormalScreen} {
    //padding: 0;
    margin: 0 -4rem 10rem;
  }
`

const HomePage = ({ data }: HomePageProps): JSX.Element => {
  const featuredEvents = data.homepageHeroItems.edges
  const contentBlocks = data.homepageBlocks.nodes[0].body
  const dynamicSetNodes = {
    whatsOnSectionItems: data.whatsOnSectionItems.edges,
    learningSectionItems: data.learningSectionItems.edges,
    exhibitionSectionItems: data.exhibitionSectionItems.edges,
  }
  const spotlightEvent = data?.spotlightEvent?.edges[0]?.node
  const spotlightPage = data?.spotlightPage?.edges[0]?.node

  return (
    <Layout noTop={true}>
      <HeroCarousel items={featuredEvents} />
      {contentBlocks?.map((block) => {
        if (
          isContentfulType<Queries.ContentfulSpotlightBlock>(
            block,
            'ContentfulSpotlightBlock',
          )
        ) {
          // biome-ignore lint/suspicious/noImplicitAnyLet: <explanation>
          let spotlight
          if (block.dynamic) {
            if (block.dynamicType === DynamicType.EVENT) {
              spotlight = spotlightEvent
            } else {
              spotlight = spotlightPage
            }
          } else {
            spotlight = block.manualSpotlight?.[0]
          }

          // Check that spotlight is not undefined
          if (!spotlight) return null

          // Currently, Spotlight can be page, custom page, or event
          const isPage = spotlight.__typename === 'ContentfulPage'
          const isCustomPage = spotlight.__typename === 'ContentfulCustomPage'

          // Handle categories for ContentfulPage
          const categories =
            isPage && (spotlight.categories || spotlight.category || undefined)

          // Handle slug based on page or custom page type
          const slug = isPage
            ? [
                spotlight.category?.pageCategoryParent?.slug,
                spotlight.category?.slug,
                spotlight.slug,
              ]
                .filter((_) => _)
                .join('/')
            : isCustomPage
              ? spotlight.slug // For custom page, just use slug directly
              : spotlight.slug // For event, use the slug

          // Custom page might have linked hero blocks, try to find and use heroPageHeaderBlock's heroImage
          const heroImage = isCustomPage
            ? spotlight.body?.find(
                (block) => block.__typename === 'ContentfulHeroPageHeaderBlock',
              )?.heroImage || null // Fallback to null if no hero image found
            : spotlight.imageWithFocalPoint?.image || spotlight.heroImage // For regular page

          // Focal ratio only applies to regular page's imageWithFocalPoint
          const focalRatio =
            isPage && spotlight.imageWithFocalPoint
              ? GetFocalRatio(spotlight.imageWithFocalPoint)
              : isCustomPage && heroImage && heroImage.focalPoint
                ? GetFocalRatio(heroImage)
                : undefined

          const description = isPage
            ? spotlight.leadingParagraph?.childMarkdownRemark
                ?.rawMarkdownBody || ''
            : isCustomPage
              ? spotlight.body?.find(
                  (block) => block.__typename === 'ContentfulPageHeaderBlock',
                )?.leadingParagraph.childMarkdownRemark?.rawMarkdownBody || null
              : ''

          // Render the spotlight block
          return (
            <BlockContainer key={block.contentful_id}>
              <StyledEmbeddedEntry noPadding={true}>
                <Spotlight
                  preTitle={spotlight.preTitle}
                  title={spotlight.title}
                  slug={slug}
                  image={isCustomPage ? heroImage.image : heroImage} // Image from either page, custom page's block, or fallback
                  focalRatio={focalRatio} // Only applicable for regular pages
                  leadingParagraph={description}
                  type={
                    isPage || isCustomPage ? 'page' : spotlight.eventType.slug
                  }
                  categories={categories} // Only for pages
                  isFlipped={block.flipOrder}
                />
              </StyledEmbeddedEntry>
            </BlockContainer>
          )
        }

        if (
          isContentfulType<Queries.ContentfulContentTileBlock>(
            block,
            'ContentfulContentTileBlock',
          )
        ) {
          return (
            <BlockContainer key={block.contentful_id}>
              <StyledEmbeddedEntry noPadding={true}>
                <ContentSet
                  block={block}
                  dynamicSetNodes={dynamicSetNodes}
                  short
                />
              </StyledEmbeddedEntry>
            </BlockContainer>
          )
        }

        if (
          isContentfulType<Queries.ContentfulCuratedContentTileBlock>(
            block,
            'ContentfulCuratedContentTileBlock',
          )
        ) {
          return (
            <BlockContainer key={block.contentful_id}>
              <StyledEmbeddedEntry noPadding={true}>
                <ContentSet block={block} nodes={block.tiles} short />
              </StyledEmbeddedEntry>
            </BlockContainer>
          )
        }

        if (
          isContentfulType<Queries.ContentfulImageGridBlock>(
            block,
            'ContentfulImageGridBlock',
          )
        ) {
          return (
            <Container key={block.contentful_id}>
              <ImageGridBlock data={block} />
            </Container>
          )
        }

        if (
          isContentfulType<Queries.ContentfulChatBotBlock>(
            block,
            'ContentfulChatBotBlock',
          ) &&
          isChatbotEnabled()
        ) {
          return (
            <BlockContainer key={block.contentful_id}>
              <BlockWrapper>
                <ChatBot endpoint={process.env.GATSBY_CHATBOT_ENDPOINT} />
              </BlockWrapper>
            </BlockContainer>
          )
        }
        if (
          isContentfulType<Queries.ContentfulIframeBlock>(
            block,
            'ContentfulIframeBlock',
          )
        ) {
          return (
            <Container key={block.contentful_id}>
              <IframeBlock data={block} />
            </Container>
          )
        }
      })}
    </Layout>
  )
}

export default HomePage

export const Head = ({ data }: HomePageProps) => {
  const featuredEvents = data.homepageHeroItems.edges
  const imageUrl =
    featuredEvents?.[0]?.node?.heroImage &&
    getSrc(featuredEvents[0]?.node?.heroImage)
  return <SEO imageUrl={imageUrl} />
}
export const contentfulPageFragment = graphql`
  fragment ContentfulPageFragment on ContentfulPage {
    __typename
    title
    slug
    category {
      slug
      colour
      title
      pageCategoryParent {
        slug
      }
    }
    heroImage {
      title
      url
      gatsbyImageData(layout: CONSTRAINED, quality: 80)
    }
    imageWithFocalPoint {
      focalPoint {
        focalPoint {
          x
          y
        }
      }
      image {
        file {
          details {
            image {
              height
              width
            }
          }
        }
        url
        gatsbyImageData(layout: CONSTRAINED, quality: 80)
      }
    }
    leadingParagraph {
      childMarkdownRemark {
        rawMarkdownBody
      }
    }
  }
`
export const contentfulEventFragment = graphql`
  fragment ContentfulEventFragment on ContentfulEvent {
    __typename
    preTitle
    title
    slug
    leadingParagraph {
      childMarkdownRemark {
        rawMarkdownBody
      }
    }
    heroImage {
      title
      url
      gatsbyImageData(layout: CONSTRAINED, width: 1680, height: 1050, quality: 80)
    }
    imageWithFocalPoint {
      focalPoint {
        focalPoint {
          x
          y
        }
      }
      image {
        file {
          details {
            image {
              height
              width
            }
          }
        }
        url
        gatsbyImageData(layout: CONSTRAINED, width: 1680, height: 1050, quality: 80)
      }
    }
    eventType {
      title
      slug
    }
    categories {
      title
      slug
      id
    }
  }
`

export const contentfulSpotlightBlockFragment = graphql`
  fragment ContentfulSpotlightBlockFragment on ContentfulSpotlightBlock {
    contentful_id
    __typename
    title
    id
    dynamic
    dynamicType
    flipOrder
    manualSpotlight {
      __typename
      ... on Node {
        ... on ContentfulPage {
          ...ContentfulPageFragment
        }
        ... on ContentfulEvent {
          ...ContentfulEventFragment
        }
      }
    }
  }
`

export const contentfulContentTileBlockFragment = graphql`
  fragment ContentfulContentTileBlockFragment on ContentfulContentTileBlock {
    id
    __typename
    title
    contentful_id
    sys {
      contentType {
        sys {
          type
        }
      }
    }
    link
    linkUrl
    dynamicContent
  }
`

export const contentfulCuratedContentTileBlockFragment = graphql`
  fragment ContentfulCuratedContentTileBlockFragment on ContentfulCuratedContentTileBlock {
    id
    __typename
    title
    contentful_id
    sys {
      contentType {
        sys {
          type
        }
      }
    }
    link
    linkUrl
    tiles {
      ... on Node {
        ... on ContentfulPage {
          ...ContentfulPageFragment
        }
        ... on ContentfulEvent {
          ...ContentfulEventFragment
        }
      }
    }
  }
`

export const query = graphql`
  query HomePage($now: Date) {
    homepageBlocks: allContentfulCustomPage(filter: { slug: { eq: "/" } }) {
      nodes {
        body {
          ... on Node {
            ... on ContentfulContentTileBlock {
              ...ContentfulContentTileBlockFragment
            }
            ... on ContentfulCuratedContentTileBlock {
              ...ContentfulCustomCuratedContentTileBlockFragment
            }
            ... on ContentfulSpotlightBlock {
              ...ContentfulCustomSpotlightBlockFragment
            }
            ... on ContentfulImageGridBlock {
              ...ContentfulImageGridBlockFragment
            }
            ... on ContentfulChatBotBlock {
              ...ContentfulChatBotBlockFragment
            }
            ... on ContentfulPageHeaderBlock {
              ...ContentfulPageHeaderBlockFragment
            }
            ... on ContentfulHeroPageHeaderBlock {
              ...ContentfulHeroPageHeaderBlockFragment
            }
            ... on ContentfulImageGallery {
              ...ContentfulImageGalleryFragment
            }
            ... on ContentfulContentSetBlock {
              ...ContentfulContentSetBlockFragment
            }
            ... on ContentfulImageBlock {
              ...ContentfulImageBlockFragment
            }
            ... on ContentfulIframeBlock {
              id
              __typename
              title
              url
              height
            }
          }
        }
        title
        slug
      }
    }
    homepageHeroItems: allContentfulEvent(limit: 6, filter: { isFeaturedInHomepageHero: { eq: true } }) {
      edges {
        node {
          preTitle
          title
          slug
          startDate
          endDate
          customDateText
          eventType {
            title
            slug
          }
          categories {
            title
            slug
            id
            colour
          }
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, quality: 80)
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, quality: 80)
            }
          }
        }
      }
    }
    whatsOnSectionItems: allContentfulEvent(
      limit: 3
      sort: { startDate: DESC }
      filter: { endDate: { gte: $now }, startDate: { ne: null } }
    ) {
      edges {
        node {
          id
          preTitle
          title
          startDate
          endDate
          customDateText
          eventType {
            title
            slug
          }
          eventType {
            title
            slug
          }
          slug
          categories {
            title
            slug
            id
            colour
          }
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, width: 480, quality: 80)
            }
          }
        }
      }
    }
    learningSectionItems: allContentfulPage(filter: { category: { slug: { eq: "learning" } } }, limit: 3) {
      edges {
        node {
          id
          title
          slug
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, width: 480, quality: 80)
            }
          }
          category {
            title
            slug
            colour
          }
          leadingParagraph {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
        }
      }
    }
    exhibitionSectionItems: allContentfulEvent(filter: { eventType: { slug: { eq: "exhibition" } } }, limit: 3) {
      edges {
        node {
          id
          preTitle
          title
          slug
          leadingParagraph {
            childMarkdownRemark {
              rawMarkdownBody
            }
          }
          categories {
            title
            slug
            id
            colour
          }
          eventType {
            title
            slug
          }
          startDate
          endDate
          customDateText
          heroImage {
            title
            url
            gatsbyImageData(layout: CONSTRAINED, width: 480, height: 300, quality: 80)
          }
          imageWithFocalPoint {
            focalPoint {
              focalPoint {
                x
                y
              }
            }
            image {
              file {
                details {
                  image {
                    height
                    width
                  }
                }
              }
              url
              gatsbyImageData(layout: CONSTRAINED, width: 480, quality: 80)
            }
          }
        }
      }
    }
    spotlightEvent: allContentfulEvent(
      filter: { isSpotlightOnHomepage: { eq: true } }
      limit: 1
      sort: { updatedAt: DESC }
    ) {
      edges {
        node {
          ...ContentfulEventFragment
        }
      }
    }
    spotlightPage: allContentfulPage(
      filter: { isSpotlightOnHomepage: { eq: true } }
      limit: 1
      sort: { updatedAt: DESC }
    ) {
      edges {
        node {
          ...ContentfulPageFragment
        }
      }
    }
  }
`
